import { useCallback, useMemo, useState, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CustomerOrderDto, CustomerOrderStatus, PartnerDto } from '@/api';
import {
  Alert,
  Badge,
  Combobox,
  DebouncedSearchInput,
  Input,
  Select,
  Table,
  TablePagination,
  useDebouncedValue,
  useTableSort,
  type TableColumn,
} from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import {
  formatDate,
  formatInstantDate,
  formatMoney,
  toNumber,
} from '../../lib/format';
import { CUSTOMER_ORDER_STATUSES } from '../../lib/shipmentStatus';
import { formatSort } from '../../lib/tableSort';
import {
  useCustomerOrdersList,
  usePartner,
  usePartnerOptions,
} from './api/queries';
import {
  PAGE_SIZE,
  resolveCustomerOrdersSearch,
  type ActivityFilter,
  type CustomerOrdersSearch,
} from './lib/searchParams';
import { STATUS_TONES } from './lib/status';

const ROUTE_ID = '/_app/orders' as const;
const ROUTE_PATH = '/orders' as const;

/*
 * Заказы 1С - заказы клиентов УТ, только чтение: их наполняет поток из 1С
 * (architecture/contracts/onec-customer-orders.md), в том числе заказы,
 * созданные передачей отгрузки. Фильтры и сортировка - в адресе страницы.
 */
export const CustomerOrdersPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveCustomerOrdersSearch(search);

  const setSearch = useCallback(
    (patch: Partial<CustomerOrdersSearch>, resetPage = true) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: CustomerOrdersSearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = useCustomerOrdersList(resolved);

  // В адресе только id клиента: подпись поля берём из выбора, а после
  // перезагрузки страницы - запросом клиента по id.
  const [partnerQuery, setPartnerQuery] = useState('');
  const partnerOptions = usePartnerOptions(
    useDebouncedValue(partnerQuery, 300),
  );
  const [picked, setPicked] = useState<PartnerDto | null>(null);
  const loadedPartner = usePartner(
    picked?.id === resolved.partner ? undefined : resolved.partner,
  );
  const selectedPartner =
    resolved.partner === undefined
      ? null
      : picked?.id === resolved.partner
        ? picked
        : (loadedPartner.data ?? null);

  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  const columns = useMemo<ReadonlyArray<TableColumn<CustomerOrderDto>>>(
    () => [
      {
        id: 'date',
        header: 'Дата',
        width: 'w-28',
        isSortable: true,
        cell: (order) => formatInstantDate(order.date),
      },
      {
        id: 'number',
        header: 'Номер',
        mono: true,
        width: 'w-36',
        isSortable: true,
        cell: (order) => order.number,
      },
      {
        id: 'partner',
        header: 'Клиент',
        cell: (order) =>
          order.partnerName ? (
            <span className="font-[550] text-ink-900">{order.partnerName}</span>
          ) : (
            <span className="text-ink-400">не загружен из 1С</span>
          ),
      },
      {
        id: 'shipmentDate',
        header: 'Отгрузка',
        width: 'w-28',
        hideBelow: 'md',
        isSortable: true,
        cell: (order) =>
          order.shipmentDate ? formatDate(order.shipmentDate) : '',
      },
      {
        id: 'status',
        header: 'Статус',
        width: 'w-52',
        cell: (order) => (
          <div className="flex flex-col items-start gap-0.5">
            {order.status ? (
              <Badge tone={STATUS_TONES[order.status]} withDot>
                {CUSTOMER_ORDER_STATUSES[order.status]}
              </Badge>
            ) : (
              <span className="text-ink-400">без статуса</span>
            )}
            {!order.isActive ? (
              <span className="text-[11px] text-ink-500">удалён в 1С</span>
            ) : !order.isPosted ? (
              <span className="text-[11px] text-ink-500">не проведён</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'amount',
        header: 'Сумма',
        align: 'right',
        width: 'w-36',
        isSortable: true,
        cell: (order) => formatMoney(toNumber(order.amount)),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
        <DebouncedSearchInput
          className="w-[170px]"
          placeholder="Номер заказа..."
          aria-label="Поиск по номеру заказа"
          value={resolved.search}
          onCommit={(value) => setSearch({ search: value || undefined })}
        />
        <Combobox
          className="min-w-[200px] flex-1"
          aria-label="Клиент"
          value={selectedPartner}
          onChange={(partner) => {
            setPicked(partner);
            setSearch({ partner: partner?.id });
          }}
          onQueryChange={setPartnerQuery}
          options={partnerOptions.data?.items ?? []}
          isLoading={partnerOptions.isFetching}
          getKey={(partner) => partner.id}
          getLabel={(partner) => partner.name}
          placeholder="Все клиенты"
          emptyLabel="Клиент не найден"
        />
        <div className="flex items-center gap-1.5 text-xs text-ink-500">
          <span>с</span>
          <Input
            type="date"
            aria-label="Дата заказа с"
            className="w-[132px]"
            value={resolved.from}
            max={resolved.to || undefined}
            onChange={(event) =>
              setSearch({ from: event.target.value || undefined })
            }
          />
          <span>по</span>
          <Input
            type="date"
            aria-label="Дата заказа по"
            className="w-[132px]"
            value={resolved.to}
            min={resolved.from || undefined}
            onChange={(event) =>
              setSearch({ to: event.target.value || undefined })
            }
          />
        </div>
        <Select
          aria-label="Статус заказа"
          value={resolved.status ?? 'all'}
          onChange={(event) => {
            const status = event.target.value;
            setSearch({
              status:
                status === 'all' ? undefined : (status as CustomerOrderStatus),
            });
          }}
        >
          <option value="all">Все статусы</option>
          {Object.entries(CUSTOMER_ORDER_STATUSES).map(([status, label]) => (
            <option key={status} value={status}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Действие заказа"
          value={resolved.activity}
          onChange={(event) => {
            const activity = event.target.value as ActivityFilter;
            setSearch({
              activity: activity === 'active' ? undefined : activity,
            });
          }}
        >
          <option value="active">Действующие</option>
          <option value="inactive">Удалённые в 1С</option>
          <option value="all">Все</option>
        </Select>
      </div>

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить заказы: {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <Table
          columns={columns}
          rows={data?.items ?? []}
          getRowKey={(order) => order.id}
          onRowClick={(order) =>
            void navigate({
              to: '/orders/$orderId',
              params: { orderId: order.id },
            })
          }
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          emptyLabel="Заказов по выбранным фильтрам нет"
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
        />
      )}
    </>
  );
};
